<template>
  <el-dialog :title="$tc('general.print')" :visible.sync="visible" width="600px" @close="handleClose">
    <div v-if="loading" class="loading-state">{{ $tc('general.loading') }}</div>
    <div v-else-if="!types.length" class="empty-state">
      {{ $tc('tournament.no_document_types') || 'No document types yet.' }}
    </div>
    <div v-else class="document-print-options">
      <el-radio-group v-model="selectedTypeId" @change="handleTypeChange">
        <div v-for="type in types" :key="type.id" class="type-option">
          <el-radio :label="type.id">{{ type.name }}</el-radio>
          <div v-if="selectedTypeId === type.id" class="targets">
            <el-checkbox v-model="targets.competitors">{{ competitorsLabel }}</el-checkbox>
            <el-checkbox v-model="targets.clubs">{{ $tc('general.club', 2) }}</el-checkbox>
            <el-checkbox v-model="targets.coaches">{{ $tc('club.coach', 2) }}</el-checkbox>
            <el-checkbox v-model="targets.officials">{{ $tc('club.official', 2) }}</el-checkbox>
            <el-checkbox v-model="targets.referees">{{ $tc('tournament.judges') }}</el-checkbox>

            <el-checkbox v-model="manualEnabled" class="manual-toggle">
              {{ $tc('tournament.print_manually') || 'Print manually' }}
            </el-checkbox>

            <div v-if="manualEnabled" class="manual-entry">
              <table class="manual-rows-table">
                <thead>
                  <tr>
                    <th v-for="key in usedVariableKeys(type)" :key="key">{{ variableLabel(key) }}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, rowIndex) in manualRows" :key="rowIndex">
                    <td v-for="key in usedVariableKeys(type)" :key="key">
                      <el-input v-model="row[key]" size="small" />
                    </td>
                    <td><i class="el-icon-delete" @click="manualRows.splice(rowIndex, 1)" /></td>
                  </tr>
                </tbody>
              </table>
              <el-button size="small" @click="addManualRow(type)">
                {{ $tc('general.add_row') || '+ Add row' }}
              </el-button>
            </div>
          </div>
        </div>
      </el-radio-group>
    </div>

    <template v-slot:footer>
      <el-button @click="handleClose">{{ $tc('general.cancel') }}</el-button>
      <el-button type="primary" :disabled="!canPrint" @click="handlePrint">
        {{ $tc('general.print') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { canonicalizeKey } from '@/utils/documentTemplate.js'

export default {
  name: 'DocumentPrintOptionsModal',
  props: {
    value: { type: Boolean, default: false },
    tournamentId: { type: [Number, String], default: null },
    seminarId: { type: [Number, String], default: null }
  },
  data () {
    return {
      loading: false,
      types: [],
      selectedTypeId: null,
      targets: { competitors: false, clubs: false, coaches: false, officials: false, referees: false },
      manualEnabled: false,
      manualRows: []
    }
  },
  computed: {
    visible: {
      get () { return this.value },
      set (val) { this.$emit('input', val) }
    },
    competitorsLabel () {
      return this.seminarId ? (this.$tc('tournament.participants') || 'Participants') : this.$tc('tournament.competitors', 2)
    },
    canPrint () {
      if (!this.selectedTypeId) return false
      if (this.manualEnabled) return this.manualRows.length > 0
      return Object.values(this.targets).some(Boolean)
    }
  },
  watch: {
    value (newVal) {
      if (newVal) this.loadTypes()
    }
  },
  methods: {
    async loadTypes () {
      this.loading = true
      try {
        this.types = this.seminarId
          ? await this.$store.dispatch('seminars/getDocumentTypes', this.seminarId)
          : await this.$store.dispatch('tournaments/getDocumentTypes', this.tournamentId)
      } finally {
        this.loading = false
      }
    },
    handleTypeChange () {
      this.targets = { competitors: false, clubs: false, coaches: false, officials: false, referees: false }
      this.manualEnabled = false
      this.manualRows = []
    },
    handleClose () {
      this.visible = false
      this.selectedTypeId = null
      this.targets = { competitors: false, clubs: false, coaches: false, officials: false, referees: false }
      this.manualEnabled = false
      this.manualRows = []
    },
    usedVariableKeys (type) {
      const keys = new Set()
      const pattern = /\{\{(\w+)\}\}/g
      ;(type.fields || []).forEach(field => {
        let match
        while ((match = pattern.exec(field.template || '')) !== null) {
          const key = canonicalizeKey(match[1])
          if (key) keys.add(key)
        }
      })
      return Array.from(keys)
    },
    variableLabel (key) {
      const labels = {
        competitorName: this.$tc('tournament.competitor_name') || 'Ime takmičara',
        club: this.$tc('general.club', 1) || 'Klub',
        competition: this.$tc('tournament.competition') || 'Naziv turnira',
        position: this.$tc('tournament.place_position') || 'Pozicija',
        discipline: this.$tc('tournament.discipline') || 'Disciplina',
        category: this.$tc('tournament.category') || 'Kategorija',
        belt: this.$tc('form.belt') || 'Pojas',
        role: this.$tc('tournament.role') || 'Uloga'
      }
      return labels[key] || key
    },
    addManualRow (type) {
      const row = {}
      this.usedVariableKeys(type).forEach(key => { row[key] = '' })
      this.manualRows.push(row)
    },
    handlePrint () {
      this.$emit('print', {
        documentTypeId: this.selectedTypeId,
        targets: { ...this.targets },
        manual: { enabled: this.manualEnabled, rows: this.manualEnabled ? this.manualRows : [] }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.loading-state, .empty-state {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}
.type-option {
  margin-bottom: 16px;
  .targets {
    margin: 8px 0 0 24px;
    // el-radio-group (the ancestor this lives under) sets font-size: 0 to
    // collapse whitespace gaps between its radio buttons - that cascades
    // down through plain elements like our manual-entry <th>/<td> text
    // (Element UI's own radio/checkbox labels reset their own font-size,
    // which is why those stayed visible while our table headers rendered
    // at 0px and looked blank). Reset it back here.
    font-size: 14px;
    .el-checkbox {
      display: block;
      margin-bottom: 6px;
    }
  }
}
.manual-toggle {
  display: block;
  margin: 10px 0;
}
.manual-entry {
  .manual-rows-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 10px;
    th, td {
      padding: 4px;
      text-align: left;
    }
    td i {
      cursor: pointer;
      &:hover { color: #f56c6c; }
    }
  }
}
</style>
